import { inject, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ConfirmationService } from 'primeng/api';
import { logout } from '../../+state/auth.actions';

/**
 * Service to handle session expiration notifications.
 * Shows a modal dialog informing the user that their session has expired,
 * with an automatic redirect after a configurable timeout.
 */
@Injectable({
  providedIn: 'root',
})
export class SessionExpiredService {
  private readonly confirmationService = inject(ConfirmationService);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly ngZone = inject(NgZone);

  private isShowingExpiredDialog = false;
  private autoRedirectTimer: ReturnType<typeof setTimeout> | null = null;

  /** Auto-redirect timeout in milliseconds (10 seconds) */
  private readonly AUTO_REDIRECT_TIMEOUT = 10000;

  /**
   * Shows the session expired dialog to the user.
   * Prevents duplicate dialogs if one is already visible.
   * Automatically redirects to login after 10 seconds if user doesn't interact.
   */
  showSessionExpiredDialog(): void {
    if (this.isShowingExpiredDialog) {
      return;
    }

    this.isShowingExpiredDialog = true;

    try {
      this.ngZone.run(() => {
        this.confirmationService.confirm({
          key: 'sessionExpired',
          header: 'Sesión expirada',
          message:
            'Su sesión ha expirado. Por favor, vuelva a iniciar sesión para continuar.',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Aceptar',
          rejectVisible: false,
          accept: () => {
            this.clearAutoRedirectTimer();
            this.handleSessionExpiredAccept();
          },
        });
      });

      this.startAutoRedirectTimer();
    } catch (error) {
      console.error('Error showing session expired dialog:', error);
      this.isShowingExpiredDialog = false;
      this.performLogoutAndRedirect();
    }
  }

  /**
   * Handles the accept action from the session expired dialog.
   * Dispatches logout, clears storage, and redirects to login.
   */
  private handleSessionExpiredAccept(): void {
    this.clearAutoRedirectTimer();
    this.performLogoutAndRedirect();
  }

  /**
   * Starts the auto-redirect timer that triggers after 10 seconds.
   * If the user doesn't interact with the dialog, they are redirected automatically.
   */
  private startAutoRedirectTimer(): void {
    this.clearAutoRedirectTimer();

    this.autoRedirectTimer = setTimeout(() => {
      this.ngZone.run(() => {
        this.confirmationService.close();
        this.performLogoutAndRedirect();
      });
    }, this.AUTO_REDIRECT_TIMEOUT);
  }

  /**
   * Clears the auto-redirect timer if it's active.
   */
  private clearAutoRedirectTimer(): void {
    if (this.autoRedirectTimer) {
      clearTimeout(this.autoRedirectTimer);
      this.autoRedirectTimer = null;
    }
  }

  /**
   * Performs the logout action, clears session storage, and navigates to login.
   */
  private performLogoutAndRedirect(): void {
    this.clearAutoRedirectTimer();
    this.isShowingExpiredDialog = false;
    localStorage.removeItem('KanziWMSUserToken');
    this.store.dispatch(logout());
    this.router.navigate(['/auth/login']);
  }
}
